import React, {
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import { Box, IconButton, Stack, Switch } from "@mui/joy";
import TabButtonList from "components/common/TabButtonList";
import TaskContentV2 from "components/task/content/v2/TaskContentV2";
import { ProgramContentType } from "data/BlockComponent";
import { theme } from "styles/theme";
import { buildTaskEditorProgram } from "./taskEditorRuntime";
import { TaskEditorState } from "./taskEditorUtils";

const previewTabs = [
  { id: "patient", label: "프리뷰 - 내담자" },
  { id: "coach", label: "프리뷰 - 코치" },
] as const;

type TaskEditorPreviewTabId = (typeof previewTabs)[number]["id"];

const TASK_EDITOR_PREVIEW_SYNC_DELAY_MS = 300;

function isPreviewObject(
  value: unknown
): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function clonePreviewValue<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map((item) => clonePreviewValue(item)) as T;
  }

  if (isPreviewObject(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [key, clonePreviewValue(item)])
    ) as T;
  }

  return value;
}

function normalizePreviewLine(
  line: unknown
): ProgramContentType["lines"][number] {
  if (!Array.isArray(line)) {
    return [];
  }

  return line
    .filter(isPreviewObject)
    .map(
      (cell) =>
        clonePreviewValue(cell) as unknown as ProgramContentType["lines"][number][number]
    );
}

function normalizePreviewContent(content: ProgramContentType[] | undefined) {
  if (!Array.isArray(content)) {
    return [];
  }

  return content
    .filter(isPreviewObject)
    .map((block) => {
      const normalizedBlock = clonePreviewValue(block) as ProgramContentType;
      const normalizedLines = Array.isArray(normalizedBlock.lines)
        ? normalizedBlock.lines.map((line) => normalizePreviewLine(line)).filter((line) => line.length > 0)
        : [];

      return {
        ...normalizedBlock,
        lines: normalizedLines,
      };
    })
    .filter((block) => block.lines.length > 0);
}

function mergePreviewValue(previousValue: unknown, nextValue: unknown): unknown {
  if (Array.isArray(nextValue)) {
    const previousArray = Array.isArray(previousValue) ? previousValue : [];

    return nextValue.map((item, index) =>
      mergePreviewValue(previousArray[index], item)
    );
  }

  if (isPreviewObject(nextValue)) {
    const previousObject = isPreviewObject(previousValue) ? previousValue : {};

    return Object.fromEntries(
      Object.entries({ ...previousObject, ...nextValue }).map(([key, value]) => [
        key,
        key in nextValue
          ? mergePreviewValue(previousObject[key], nextValue[key])
          : clonePreviewValue(value),
      ])
    );
  }

  return nextValue === undefined ? clonePreviewValue(previousValue) : nextValue;
}

function getPreviewBlockSignature(block: ProgramContentType) {
  const normalizedLines = Array.isArray(block.lines)
    ? block.lines.map((line) => (Array.isArray(line) ? line : []))
    : [];

  return JSON.stringify({
    noBorder: block.noBorder,
    noArrow: block.noArrow,
    isEnd: block.isEnd,
    indentation: block.indentation,
    hideIfPatient: block.hideIfPatient,
    hideIfCoach: block.hideIfCoach,
    removable: block.removable,
    disableGoNext: block.disableGoNext,
    isHighlight: block.isHighlight,
    isAlwaysHidden: block.isAlwaysHidden,
    autoOpenNext: block.autoOpenNext,
    nextBlockId: block.nextBlockId,
    lineTypes: normalizedLines.map((line) => line.map((cell) => cell?.type)),
  });
}

function getPreviewBlockKey(block: ProgramContentType, index: number) {
  return block.blockId ? `block:${block.blockId}` : `index:${index}`;
}

function shouldMergePreviewBlock(
  previousBlock: ProgramContentType | undefined,
  nextBlock: ProgramContentType
) {
  return (
    Boolean(previousBlock) &&
    getPreviewBlockSignature(previousBlock as ProgramContentType) ===
      getPreviewBlockSignature(nextBlock)
  );
}

function getAllShownPreviewContent(content: ProgramContentType[]) {
  return content.map((block) => ({
    ...clonePreviewValue(block),
    isShown: true,
  }));
}

function mergePreviewContent(
  previousContent: ProgramContentType[] | undefined,
  nextContent: ProgramContentType[]
) {
  if (!previousContent) {
    return clonePreviewValue(nextContent);
  }

  const previousBlocksByKey = new Map(
    previousContent.map((block, index) => [getPreviewBlockKey(block, index), block])
  );
  const matchedBlockKeys = new Set<string>();

  const mergedContent = nextContent.map((nextBlock, index) => {
    const nextBlockKey = getPreviewBlockKey(nextBlock, index);
    const previousBlockFromKey = previousBlocksByKey.get(nextBlockKey);
    const previousBlock =
      previousBlockFromKey ??
      (nextBlock.blockId ? undefined : previousContent[index]);

    if (previousBlockFromKey && shouldMergePreviewBlock(previousBlock, nextBlock)) {
      matchedBlockKeys.add(nextBlockKey);
      return mergePreviewValue(previousBlock, nextBlock) as ProgramContentType;
    }

    if (!previousBlockFromKey && shouldMergePreviewBlock(previousBlock, nextBlock)) {
      matchedBlockKeys.add(nextBlockKey);
      return mergePreviewValue(previousBlock, nextBlock) as ProgramContentType;
    }

    return clonePreviewValue(nextBlock);
  });

  const lastShownIndex = mergedContent.reduce(
    (shownIndex, block, index) => (block.isShown ? index : shownIndex),
    -1
  );

  return mergedContent.map((block, index) => {
    const blockKey = getPreviewBlockKey(block, index);

    return index < lastShownIndex && !matchedBlockKeys.has(blockKey)
      ? {
          ...block,
          isShown: true,
        }
      : block;
  });
}

function TaskEditorLivePreview({ task }: { task: TaskEditorState }) {
  const [viewerMode, setViewerMode] =
    useState<TaskEditorPreviewTabId>("patient");
  const [isPatientTrialPreviewEnabled, setIsPatientTrialPreviewEnabled] =
    useState(false);
  const [previewTask, setPreviewTask] = useState(task);
  const previousTaskIdRef = useRef(task?.taskId);

  useEffect(() => {
    const syncTimer = window.setTimeout(() => {
      setPreviewTask(task);
    }, TASK_EDITOR_PREVIEW_SYNC_DELAY_MS);

    return () => {
      window.clearTimeout(syncTimer);
    };
  }, [task]);

  const taskProgram = useMemo(
    () => buildTaskEditorProgram(previewTask),
    [previewTask]
  );
  const normalizedTaskProgramContent = useMemo(
    () => {
      const content = taskProgram?.content;
      return normalizePreviewContent(Array.isArray(content) ? content : []);
    },
    [taskProgram]
  );
  const [contentData, setContentData] = useState<
    ProgramContentType[] | undefined
  >(() => {
    const normalized = Array.isArray(normalizedTaskProgramContent) ? normalizedTaskProgramContent : [];
    return clonePreviewValue(normalized);
  });
  const [moveToIndex, setMoveToIndex] = useState<number | undefined>(undefined);

  useEffect(() => {
    setContentData((previousContent) => {
      const normalized = Array.isArray(normalizedTaskProgramContent) ? normalizedTaskProgramContent : [];
      return previousTaskIdRef.current !== taskProgram?.taskId
        ? clonePreviewValue(normalized)
        : mergePreviewContent(previousContent, normalized);
    });
    previousTaskIdRef.current = taskProgram?.taskId;
  }, [normalizedTaskProgramContent, taskProgram]);

  useEffect(() => {
    setMoveToIndex(undefined);
  }, [taskProgram?.taskId, isPatientTrialPreviewEnabled, viewerMode]);

  const displayedContentData = useMemo(() => {
    if (!Array.isArray(contentData)) {
      return [];
    }

    return viewerMode === "patient" && !isPatientTrialPreviewEnabled
      ? getAllShownPreviewContent(contentData)
      : contentData;
  }, [contentData, isPatientTrialPreviewEnabled, viewerMode]);

  const restartTrialPreview = () => {
    const normalized = Array.isArray(normalizedTaskProgramContent) ? normalizedTaskProgramContent : [];
    setContentData(clonePreviewValue(normalized));
    setMoveToIndex(undefined);
  };

  const refreshPreview = () => {
    if (viewerMode === "patient" && isPatientTrialPreviewEnabled) {
      restartTrialPreview();
      return;
    }

    const latestTaskProgram = buildTaskEditorProgram(task);
    const latestContent = latestTaskProgram?.content;
    const normalizedLatestTaskProgramContent = normalizePreviewContent(
      Array.isArray(latestContent) ? latestContent : []
    );

    setPreviewTask(task);
    setContentData(clonePreviewValue(normalizedLatestTaskProgramContent));
    setMoveToIndex(undefined);
  };

  return (
    <Stack spacing={1} sx={{ height: "100%", minHeight: 0 }}>
      <Box
        sx={{
          width: "100%",
          maxWidth: "100%",
          mx: "auto",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "48px",
        }}
      >
        <TabButtonList
          selectedTab={viewerMode}
          data={previewTabs.map((tab) => ({
            ...tab,
            onClick: () => setViewerMode(tab.id),
          }))}
        />
      </Box>
      {viewerMode === "patient" && (
        <Box
          sx={{
            width: "100%",
            maxWidth: "100%",
            mx: "auto",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "42px",
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                fontSize: "14px",
                fontWeight: 600,
                color: theme.vars.palette.Content.Weak,
                mr: 0.5,
              }}
            >
              체험 모드
            </Box>
            <Switch
              size="sm"
              color={isPatientTrialPreviewEnabled ? "primary" : "neutral"}
              checked={isPatientTrialPreviewEnabled}
              onChange={(event) =>
                setIsPatientTrialPreviewEnabled(event.target.checked)
              }
              slotProps={{
                input: {
                  "aria-label": "체험 모드",
                },
              }}
              sx={{
                "--Switch-trackWidth": "40px",
                "--Switch-trackHeight": "24px",
                "--Switch-thumbSize": "18px",
                "& .MuiSwitch-track": {
                  backgroundColor: theme.vars.palette.Surface.Backward,
                  border: "none",
                  boxShadow: "none",
                },
                "&.Mui-checked .MuiSwitch-track": {
                  backgroundColor: theme.vars.palette.Content.Highlight,
                  border: "none",
                  boxShadow: "none",
                },
                "& .MuiSwitch-thumb": {
                  backgroundColor: theme.vars.palette.Content.Negative,
                  border: "none",
                  boxShadow: "none",
                },
                "&.Mui-checked .MuiSwitch-thumb": {
                  border: "none",
                  boxShadow: "none",
                },
              }}
            />
            <Box
              sx={{
                width: "1px",
                height: "14px",
                backgroundColor: theme.vars.palette.Border.Weak,
                mx: 0.25,
              }}
            />
            <IconButton
              variant="plain"
              color="neutral"
              size="sm"
              aria-label="프리뷰 새로고침"
              onClick={refreshPreview}
              sx={{
                minWidth: "32px",
                minHeight: "32px",
                borderRadius: "8px",
                color: theme.vars.palette.Content.Highlight,
                backgroundColor: theme.vars.palette.transparent,
                "&:hover": {
                  backgroundColor: theme.vars.palette.hoverLight,
                },
              }}
            >
              <RefreshRoundedIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Box>
      )}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          justifyContent: "center",
          alignItems: "stretch",
          minHeight: 0,
          overflow: "hidden",
          borderRadius: "12px",
          backgroundColor: theme.vars.palette.Background.Level1,
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: "100%",
            mx: "auto",
          }}
        >
          <TaskContentV2
            taskKey={taskProgram?.taskId ?? ''}
            data={displayedContentData}
            setData={setContentData}
            moveToIndex={moveToIndex}
            setMoveToIndex={setMoveToIndex}
            viewerMode={viewerMode}
          />
        </Box>
      </Box>
    </Stack>
  );
}

export default memo(TaskEditorLivePreview);
